import React from 'react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import { RsvpForm } from '@/src/modules/rsvp';
import { parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';

const META: TemplateMetadata = {
  id: 'seed-send-off-soirée',
  name: 'Send-off Soirée',
  occasionType: 'SEND_OFF',
  description: 'Modern navy and teal layout for farewell celebrations.',
  isPremium: false,
  tags: ['modern', 'clean', 'farewell', 'navy'],
  colorScheme: { primary: '#0f172a', accent: '#0d9488', bg: '#f0fdfa' },
};

function fmt(iso: string | undefined): string {
  if (!iso) return '';
  const d = parseFlexibleDate(iso);
  if (!d) return iso;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(d);
  } catch {
    return iso;
  }
}

export function SendOffSoiree({ data }: { data: InvitationData }) {
  const [hero, ...gallery] = data.mediaUrls ?? [];
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';

  return (
    <div className="min-h-screen bg-slate-950 font-sans flex items-center justify-center p-8">
      <div className="w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl">
        {/* teal accent top */}
        <div className="bg-teal-500 h-1" />

        {/* dark header */}
        <div className="bg-slate-900 px-10 py-12 text-center">
          <span className="inline-block bg-teal-500/20 text-teal-300 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full border border-teal-500/30 mb-6">
            One Last Hurrah
          </span>
          <h1 className="text-5xl font-extrabold text-white leading-tight mb-3">
            {data.eventName}
          </h1>
          <p className="text-slate-400 text-sm">
            Farewell hosted by{' '}
            <span className="text-teal-400 font-semibold">{data.hostName}</span>
          </p>
        </div>

        {/* hero */}
        {hero && (
          <div className="w-full aspect-video overflow-hidden">
            <img src={hero} alt="" className="w-full h-full object-cover opacity-90" />
          </div>
        )}

        {/* details on dark bg */}
        <div className="bg-slate-900 px-10 py-10">
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
              <p className="text-xs text-teal-400 uppercase tracking-widest mb-2">📅 When</p>
              <p className="text-white font-semibold text-sm leading-snug">{fmt(data.eventDate)}</p>
            </div>
            <div className="bg-slate-800 rounded-2xl p-5 border border-slate-700">
              <p className="text-xs text-teal-400 uppercase tracking-widest mb-2">📍 Where</p>
              <p className="text-white font-semibold text-sm leading-snug">{data.eventVenue}</p>
            </div>
          </div>

          {/* teal divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-teal-500/30" />
            <span className="text-teal-500 text-sm">✦</span>
            <div className="flex-1 h-px bg-teal-500/30" />
          </div>

          {/* message */}
          {data.customMessage && (
            <div className="bg-teal-900/30 border border-teal-700/40 rounded-2xl px-8 py-6 mb-8">
              <p className="text-teal-100 italic leading-relaxed text-base">
                "{data.customMessage}"
              </p>
            </div>
          )}

          {/* gallery */}
          {gallery.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-8">
              {gallery.slice(0, 3).map((url, i) => (
                <div key={i} className="aspect-square rounded-xl overflow-hidden border border-slate-700">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}

          {/* RSVP */}
          <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
        </div>

        <div className="bg-teal-500 h-1" />
      </div>
    </div>
  );
}

registry.register({ metadata: META, component: SendOffSoiree });
